import { useEffect, useState } from 'react';
import { SECURITY_CONFIG } from '@/config/security';

interface AntiScrapingState {
  isBot: boolean;
  isHeadless: boolean;
  isDevToolsOpen: boolean;
  isScreenRecording: boolean;
  suspiciousActivity: boolean;
}

export const useAntiScraping = () => {
  const [state, setState] = useState<AntiScrapingState>({
    isBot: false,
    isHeadless: false,
    isDevToolsOpen: false,
    isScreenRecording: false,
    suspiciousActivity: false,
  });

  useEffect(() => {
    // Detect bots by user agent
    const detectBot = () => {
      const botPattern = /bot|crawler|spider|scraper|headless|phantom|selenium|puppeteer|playwright/i;
      return botPattern.test(navigator.userAgent);
    };

    // Detect headless browsers
    const detectHeadless = () => {
      // Check for missing properties in headless browsers
      if (!(window as any).chrome && /Chrome/.test(navigator.userAgent)) {
        return true;
      }

      // Check for webdriver flag
      if (navigator.webdriver) {
        return true;
      }

      // Check for automation-specific properties
      if (
        (window as any).__nightmare ||
        (window as any).__selenium_unwrapped ||
        (window as any).callPhantom ||
        (window as any)._phantom
      ) {
        return true;
      }

      return false;
    };

    // Detect DevTools (improved to reduce false positives)
    const detectDevTools = () => {
      // Use a more conservative threshold to account for browser UI
      const threshold = 200; // Increased from 160 to reduce false positives
      
      // Check both width AND height differences (more accurate)
      const widthDiff = window.outerWidth - window.innerWidth;
      const heightDiff = window.outerHeight - window.innerHeight;
      
      // Only trigger if the difference is significant in both dimensions
      // or extremely large in one dimension
      const isSuspicious = (
        (widthDiff > threshold && heightDiff > 100) ||
        (heightDiff > threshold && widthDiff > 100) ||
        widthDiff > 300 || // Very large horizontal difference
        heightDiff > 300   // Very large vertical difference
      );
      
      return isSuspicious;
    };

    // Detect screen recording (heuristic)
    const detectScreenRecording = () => {
      // Check if MediaRecorder is being used
      let recording = false;
      
      const originalGetUserMedia = navigator.mediaDevices.getUserMedia;
      navigator.mediaDevices.getUserMedia = async (...args) => {
        recording = true;
        return originalGetUserMedia.apply(navigator.mediaDevices, args);
      };

      return recording;
    };

    // Initial checks
    setState({
      isBot: SECURITY_CONFIG.botDetection.enableUserAgentCheck && detectBot(),
      isHeadless: SECURITY_CONFIG.botDetection.enableHeadlessDetection && detectHeadless(),
      isDevToolsOpen: SECURITY_CONFIG.videoProtection.enableDevToolsDetection && detectDevTools(),
      isScreenRecording: SECURITY_CONFIG.videoProtection.enableScreenRecordingDetection && detectScreenRecording(),
      suspiciousActivity: false,
    });

    // Continuous monitoring
    const interval = setInterval(() => {
      const devToolsOpen = SECURITY_CONFIG.videoProtection.enableDevToolsDetection && detectDevTools();
      
      setState(prev => ({
        ...prev,
        isDevToolsOpen: devToolsOpen,
        suspiciousActivity: prev.isBot || prev.isHeadless || devToolsOpen,
      }));
    }, 1000);

    // Monitor keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // F12 or Ctrl+Shift+I or Ctrl+Shift+J or Ctrl+Shift+C
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase()))
      ) {
        setState(prev => ({ ...prev, suspiciousActivity: true }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      clearInterval(interval);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return state;
};

// Hook to disable right-click and text selection
export const useContentProtection = (elementRef: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!elementRef.current) return;

    const element = elementRef.current;

    const preventContextMenu = (e: MouseEvent) => {
      if (SECURITY_CONFIG.contentProtection.disableTextSelection) {
        e.preventDefault();
        return false;
      }
    };

    const preventCopy = (e: ClipboardEvent) => {
      if (SECURITY_CONFIG.contentProtection.disableCopyPaste) {
        e.preventDefault();
        return false;
      }
    };

    const preventDrag = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    element.addEventListener('contextmenu', preventContextMenu);
    element.addEventListener('copy', preventCopy);
    element.addEventListener('cut', preventCopy);
    element.addEventListener('dragstart', preventDrag);

    // Apply CSS to prevent selection
    if (SECURITY_CONFIG.contentProtection.disableTextSelection) {
      element.style.userSelect = 'none';
      element.style.webkitUserSelect = 'none';
    }

    return () => {
      element.removeEventListener('contextmenu', preventContextMenu);
      element.removeEventListener('copy', preventCopy);
      element.removeEventListener('cut', preventCopy);
      element.removeEventListener('dragstart', preventDrag);
    };
  }, [elementRef]);
};
